import { useRef, useEffect, useLayoutEffect, useImperativeHandle, forwardRef, KeyboardEvent } from 'react';

import { cn } from '@/new/lib/utils';

interface TemplateBuilderInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export interface TemplateBuilderInputHandle {
  focus: () => void;
  insertPlaceholder: () => void;
}

const escapeHTML = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const FILLER_BR_ATTR = 'data-template-filler';

const isFillerBr = (node: Node): boolean => node.nodeName === 'BR' && (node as Element).hasAttribute?.(FILLER_BR_ATTR);

const valueToHTML = (val: string): string => {
  if (!val) return '';
  const tokenRegex = /\[[^\[\]]*\]/g;
  let lastIndex = 0;
  let result = '';
  let match;
  while ((match = tokenRegex.exec(val)) !== null) {
    result += escapeHTML(val.slice(lastIndex, match.index));
    result += `<span class="placeholder bg-green-50 text-green-500 rounded-sm px-1 py-0.5 -my-0.5 [box-decoration-break:clone] [-webkit-box-decoration-break:clone]">${escapeHTML(match[0])}</span>`;
    lastIndex = match.index + match[0].length;
  }
  result += escapeHTML(val.slice(lastIndex));
  let html = result.replace(/\n/g, '<br>');
  // A trailing <br> isn't rendered as a visible empty line in contenteditable
  // unless something follows it — append a phantom <br> so the new line shows.
  if (val.endsWith('\n')) html += `<br ${FILLER_BR_ATTR}="true">`;
  return html;
};

const htmlToValue = (root: HTMLElement): string => {
  const temp = root.cloneNode(true) as HTMLElement;
  temp.querySelectorAll(`br[${FILLER_BR_ATTR}]`).forEach((br) => br.remove());
  temp.querySelectorAll('br').forEach((br) => br.replaceWith(document.createTextNode('\n')));
  return temp.textContent || '';
};

// Walks the DOM treating <br> as one character (newline) so positions match
// `value` indices exactly. Used for both selection extraction and restoration.
const valuePositionFromPoint = (root: HTMLElement, container: Node, offset: number): number => {
  let pos = 0;
  let done = false;

  const visit = (node: Node) => {
    if (done) return;

    if (node === container) {
      if (node.nodeType === Node.TEXT_NODE) {
        pos += offset;
      } else {
        for (let i = 0; i < offset && !done; i++) {
          visit(node.childNodes[i]);
        }
      }
      done = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      pos += node.textContent?.length ?? 0;
    } else if (node.nodeName === 'BR') {
      if (!isFillerBr(node)) pos += 1;
    } else {
      for (const child of Array.from(node.childNodes)) {
        if (done) break;
        visit(child);
      }
    }
  };

  visit(root);
  return pos;
};

const getSelectionRange = (root: HTMLElement): { start: number; end: number } | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  return {
    start: valuePositionFromPoint(root, range.startContainer, range.startOffset),
    end: valuePositionFromPoint(root, range.endContainer, range.endOffset),
  };
};

const setValuePosition = (root: HTMLElement, pos: number) => {
  const selection = window.getSelection();
  const range = document.createRange();
  let remaining = pos;
  let placed = false;

  const visit = (node: Node) => {
    if (placed) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length ?? 0;
      if (remaining <= len) {
        range.setStart(node, remaining);
        range.collapse(true);
        placed = true;
        return;
      }
      remaining -= len;
      return;
    }

    if (node.nodeName === 'BR') {
      if (remaining === 0) {
        const parent = node.parentNode;
        if (parent) {
          const idx = Array.prototype.indexOf.call(parent.childNodes, node);
          range.setStart(parent, idx);
          range.collapse(true);
          placed = true;
        }
        return;
      }
      if (!isFillerBr(node)) remaining -= 1;
      return;
    }

    for (const child of Array.from(node.childNodes)) {
      if (placed) return;
      visit(child);
    }
  };

  visit(root);

  if (!placed) {
    range.selectNodeContents(root);
    range.collapse(false);
  }

  selection?.removeAllRanges();
  selection?.addRange(range);
};

export const TemplateBuilderInput = forwardRef<TemplateBuilderInputHandle, TemplateBuilderInputProps>(
  function TemplateBuilderInput({ value, onChange, placeholder, className, autoFocus = false }, ref) {
    const editorRef = useRef<HTMLDivElement>(null);
    const pendingCursorRef = useRef<number | null>(null);

    const spliceAt = (insert: string, sel: { start: number; end: number }, caretOffset = insert.length) => {
      const newValue = value.slice(0, sel.start) + insert + value.slice(sel.end);
      pendingCursorRef.current = sel.start + caretOffset;
      onChange(newValue);
    };

    const insertPlaceholderImpl = () => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      const sel = getSelectionRange(editorRef.current) ?? { start: value.length, end: value.length };
      spliceAt('[]', sel, 1);
    };

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      insertPlaceholder: insertPlaceholderImpl,
    }));

    useEffect(() => {
      if (autoFocus) editorRef.current?.focus();
    }, []);

    // Re-render the DOM on every value change so freshly typed `[label]` text
    // picks up chip styling immediately. Cursor is restored from the live position
    // (or from pendingCursorRef when an action set an explicit target).
    useLayoutEffect(() => {
      const el = editorRef.current;
      if (!el) return;

      const pending = pendingCursorRef.current;
      pendingCursorRef.current = null;

      const isFocused = document.activeElement === el || el.contains(document.activeElement);
      const cursorPos = pending ?? (isFocused ? (getSelectionRange(el)?.end ?? null) : null);

      el.innerHTML = valueToHTML(value);

      if (cursorPos !== null && (isFocused || pending !== null)) {
        setValuePosition(el, cursorPos);
        // The innerHTML rewrite kills the browser's native scroll-cursor-into-view
        // behavior, so we replicate it manually.
        scrollCursorIntoView(el, cursorPos);
      }
    }, [value]);

    const scrollCursorIntoView = (el: HTMLElement, cursorPos: number) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      let rangeRect = selection.getRangeAt(0).getBoundingClientRect();

      // Collapsed ranges at line ends or between elements can report a zero rect —
      // insert a temporary marker to get a real position, then restore the cursor.
      if (rangeRect.top === 0 && rangeRect.bottom === 0) {
        const marker = document.createElement('span');
        selection.getRangeAt(0).insertNode(marker);
        rangeRect = marker.getBoundingClientRect();
        marker.remove();
        el.normalize();
        setValuePosition(el, cursorPos);
      }

      const elRect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);
      const visibleTop = elRect.top + paddingTop;
      const visibleBottom = elRect.bottom - paddingBottom;
      if (rangeRect.bottom > visibleBottom) {
        el.scrollTop += rangeRect.bottom - visibleBottom;
      } else if (rangeRect.top < visibleTop) {
        el.scrollTop -= visibleTop - rangeRect.top;
      }
    };

    const handleInput = () => {
      if (!editorRef.current) return;
      onChange(htmlToValue(editorRef.current));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!editorRef.current) return;
      const sel = getSelectionRange(editorRef.current);
      if (!sel) return;

      if (e.key === '[') {
        e.preventDefault();
        spliceAt('[]', sel, 1);
        return;
      }

      if (e.key === ']' && sel.start === sel.end && value[sel.start] === ']') {
        e.preventDefault();
        setValuePosition(editorRef.current, sel.start + 1);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        spliceAt('\n', sel);
        return;
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!editorRef.current) return;
      const text = e.clipboardData.getData('text/plain');
      const sel = getSelectionRange(editorRef.current);
      if (!sel) return;
      spliceAt(text, sel);
    };

    const isEmpty = !value;

    return (
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        className={cn(
          'relative overflow-y-auto rounded-lg border border-gray-200 bg-white px-3.75 py-3 text-sm ring-offset-2 ring-offset-gray-50 focus-within:ring-teal-500 focus:outline-none focus-visible:ring-1',
          isEmpty &&
            'before:pointer-events-none before:absolute before:text-gray-400/80 before:content-[attr(data-placeholder)]',
          className,
        )}
      />
    );
  },
);
